import React, { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { FaTrash, FaPlus, FaVideo, FaImage, FaCloudUploadAlt, FaArrowLeft, FaArrowRight, FaFilter } from "react-icons/fa";
import { GiSparkles } from "react-icons/gi";
import { motion } from "framer-motion";
import { server } from "../server";
import { uploadMediaToServer } from "../utils/uploadMediaToServer";
import Loading from "../Components/global/Loading";
import {
  getAllCategoriesList,
  getAllGalleryMedias,
} from "../redux/actions/contents";
import { getYoutubeEmbedUrl } from "../utils/youTube";
import { deleteMediaFromServer } from "../utils/deleteMediaFromServer";
import PageName from "../Components/global/PageName";
import compressImage from "../utils/compressAsset";

export default function GalleryManager() {
  const dispatch = useDispatch();
  const {
    galleryList,
    categoriesList,
    loading: galleryLoading,
  } = useSelector((state) => state?.content);
  const { register, handleSubmit, watch, reset } = useForm();
  const [preview, setPreview] = useState(null);
  const mediaType = watch("mediaType");
  const [loading, setLoading] = useState(false);
  const [sync, setSync] = useState(0);

  // Media Filter & Category Filter States
  const [filterMediaType, setFilterMediaType] = useState("all"); // 'all' | 'image' | 'video'
  const [filterCategory, setFilterCategory] = useState("all");

  // Pagination states
  const [adminPageNo, setAdminPageNo] = useState(1);
  const ADMIN_ITEMS_PER_PAGE = 6;

  useEffect(() => {
    dispatch(getAllGalleryMedias(1, 100));
    dispatch(getAllCategoriesList(1, 100));
  }, [dispatch, sync]);

  // Filtered List based on Media Type & Category Filter
  const filteredGalleryList = useMemo(() => {
    if (!galleryList) return [];
    return galleryList.filter((item) => {
      const typeMatch = filterMediaType === "all" || item.mediaType === filterMediaType;
      const catMatch = filterCategory === "all" || String(item.categoryId) === String(filterCategory);
      return typeMatch && catMatch;
    });
  }, [galleryList, filterMediaType, filterCategory]);

  // Compute Total Pages & Slice for Admin
  const totalAdminPages = Math.ceil(filteredGalleryList.length / ADMIN_ITEMS_PER_PAGE) || 1;

  const currentAdminSlice = useMemo(() => {
    const startIdx = (adminPageNo - 1) * ADMIN_ITEMS_PER_PAGE;
    return filteredGalleryList.slice(startIdx, startIdx + ADMIN_ITEMS_PER_PAGE);
  }, [filteredGalleryList, adminPageNo]);

  // Handle filter changes
  const handleMediaFilterChange = (type) => {
    setFilterMediaType(type);
    setAdminPageNo(1);
  };

  const handleCategoryFilterChange = (catId) => {
    setFilterCategory(catId);
    setAdminPageNo(1);
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      let mediaUrl = data.mediaUrl;

      if (data?.mediaType === "image") {
        const file = await compressImage(data.media[0]);
        mediaUrl = await uploadMediaToServer(file, "null");
      }

      const { data: resData } = await axios.post(
        `${server}/gallery/new`,
        null,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            categoryId: data.categoryId,
            mediaType: data.mediaType,
            caption: data.caption,
            mediaUrl,
          },
        }
      );

      toast.success(resData?.message || "Gallery item uploaded successfully!");
      reset({ mediaUrl: "", caption: "" });
      setPreview(null);
      setSync((prev) => prev + 1);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, imageUrl) => {
    const userRes = window.confirm(
      "Are you sure you want to delete this gallery artwork?"
    );
    if (!userRes) return;

    try {
      setLoading(true);
      if (imageUrl) await deleteMediaFromServer(imageUrl);
      await axios.delete(`${server}/gallery/deleteById`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          mediaId: id,
        },
      });
      toast.success("Gallery item deleted.");
      setSync((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-16 bg-[#0A0A0C] text-[#F8F5F2]">
      <PageName
        title="Gallery Manager"
        subTitle="Upload and filter portfolio high-res photos and video embeds."
      />

      <div className="max-w-7xl mx-auto px-4 mt-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Upload Form Card */}
          <div className="lg:col-span-5 xl:col-span-4">
            <div className="bg-[#121215]/90 backdrop-blur-2xl p-6 md:p-7 rounded-3xl border border-[#C8A165]/25 shadow-[0_15px_40px_rgba(0,0,0,0.7)] sticky top-24 text-left">
              <div className="flex items-center gap-2 mb-1 text-[#C8A165]">
                <GiSparkles size={18} />
                <h3 className="text-base font-bold text-[#F8F5F2]">
                  Add Portfolio Artwork
                </h3>
              </div>
              <p className="text-xs text-[#F8F5F2]/50 mb-6">
                Categorize your work and upload photos or YouTube reels.
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* Category Selection */}
                <div>
                  <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                    Select Category
                  </label>
                  <select
                    {...register("categoryId", { required: true })}
                    className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] transition-all"
                  >
                    <option value="" className="bg-[#18181D]">Select Category</option>
                    {categoriesList?.map((e, i) => (
                      <option value={e?.id} key={i} className="bg-[#18181D]">
                        {e?.category}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Media Type Selection */}
                <div>
                  <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                    Media Type
                  </label>
                  <select
                    {...register("mediaType", { required: true })}
                    className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] transition-all"
                  >
                    <option value="" className="bg-[#18181D]">Select Media Type</option>
                    <option value="image" className="bg-[#18181D]">Image Photo</option>
                    <option value="video" className="bg-[#18181D]">YouTube Video Reel</option>
                  </select>
                </div>

                {/* File Upload for Image */}
                {mediaType === "image" && (
                  <div>
                    <label className="block text-xs font-semibold text-[#C8A165] mb-1.5 flex items-center gap-1">
                      <FaCloudUploadAlt /> Upload Photo Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      {...register("media", { required: true })}
                      className="w-full text-xs p-2.5 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] transition-all file:mr-3 file:py-1 file:px-3 file:rounded-xl file:border-0 file:text-[0.65rem] file:font-semibold file:bg-[#C8A165]/20 file:text-[#C8A165]"
                      onChange={(e) =>
                        e.target.files?.[0] &&
                        setPreview(URL.createObjectURL(e.target.files[0]))
                      }
                    />
                    {preview && (
                      <div className="mt-3 relative rounded-2xl border border-[#C8A165]/30 overflow-hidden">
                        <img
                          src={preview}
                          alt="Preview"
                          className="w-full h-40 object-cover"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* URL Input for Video */}
                {mediaType === "video" && (
                  <div>
                    <label className="block text-xs font-semibold text-[#C8A165] mb-1.5 flex items-center gap-1">
                      <FaVideo /> YouTube Video Link
                    </label>
                    <input
                      {...register("mediaUrl", { required: true })}
                      className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] placeholder:text-[#F8F5F2]/30 transition-all"
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>
                )}

                {/* Caption */}
                <div>
                  <label className="block text-xs font-semibold text-[#F8F5F2]/80 mb-1.5">
                    Caption / Title
                  </label>
                  <input
                    {...register("caption")}
                    className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] placeholder:text-[#F8F5F2]/30 transition-all"
                    placeholder="Enter caption or artwork name..."
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-bold text-xs tracking-widest uppercase shadow-lg text-[#0A0A0C] transition-all disabled:opacity-50 mt-4 cursor-pointer"
                  style={{
                    background: "linear-gradient(135deg, #C8A165 0%, #F5E5C9 50%, #9E7A48 100%)",
                  }}
                >
                  <FaPlus size={12} /> Add to Gallery
                </motion.button>
              </form>
            </div>
          </div>

          {/* Gallery Media Grid & Filters */}
          <div className="lg:col-span-7 xl:col-span-8">
            <div className="bg-[#121215]/90 backdrop-blur-2xl p-6 md:p-7 rounded-3xl border border-[#C8A165]/25 shadow-[0_15px_40px_rgba(0,0,0,0.7)] text-left flex flex-col justify-between min-h-[550px]">
              <div>
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 pb-4 border-b border-[#C8A165]/15">
                  <div>
                    <h3 className="text-base font-bold text-[#F8F5F2] flex items-center gap-2">
                      <FaImage className="text-[#C8A165]" /> Gallery Artworks
                    </h3>
                    <p className="text-xs text-[#F8F5F2]/50 mt-0.5">
                      Showing {filteredGalleryList.length} of {galleryList?.length || 0} total media
                    </p>
                  </div>
                  <span className="bg-[#C8A165]/15 text-[#C8A165] border border-[#C8A165]/30 px-3 py-1 rounded-full font-bold text-xs tracking-wider">
                    Filtered: {filteredGalleryList.length}
                  </span>
                </div>

                {/* ══════════════════════════════════════════
                    MEDIA FILTER CONTROL BAR
                   ══════════════════════════════════════════ */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-6 bg-[#16161C] p-3 rounded-2xl border border-[#C8A165]/15">
                  {/* Media Type Filter Tabs */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[0.68rem] uppercase font-bold text-[#C8A165] flex items-center gap-1 mr-1">
                      <FaFilter size={10} /> Type:
                    </span>
                    {[
                      { id: "all", label: "All Media" },
                      { id: "image", label: "Photos" },
                      { id: "video", label: "Videos" },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => handleMediaFilterChange(tab.id)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                          filterMediaType === tab.id
                            ? "bg-[#C8A165] text-[#0A0A0C] font-bold shadow-md"
                            : "text-[#F8F5F2]/60 hover:text-[#C8A165] hover:bg-white/5"
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Category Filter Dropdown */}
                  <div className="flex items-center gap-2">
                    <span className="text-[0.68rem] uppercase font-bold text-[#C8A165]">Category:</span>
                    <select
                      value={filterCategory}
                      onChange={(e) => handleCategoryFilterChange(e.target.value)}
                      className="text-xs px-3 py-1.5 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-xl focus:outline-none focus:border-[#C8A165]"
                    >
                      <option value="all">All Categories</option>
                      {categoriesList?.map((cat) => (
                        <option value={cat.id} key={cat.id}>
                          {cat.category}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {filteredGalleryList.length === 0 ? (
                  <div className="text-center py-16 text-[#F8F5F2]/40 text-xs bg-[#16161C] rounded-2xl border border-dashed border-[#C8A165]/20 p-8">
                    No gallery items match the selected media or category filter.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
                    {currentAdminSlice?.map((item, index) => (
                      <motion.div
                        key={item?.id || item?._id || index}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.04 }}
                        className="bg-[#16161C] border border-[#C8A165]/20 rounded-2xl overflow-hidden relative group hover:border-[#C8A165]/60 hover:shadow-[0_10px_30px_rgba(200,161,101,0.15)] transition-all flex flex-col justify-between"
                      >
                        <button
                          className="absolute top-2.5 right-2.5 bg-red-500/20 hover:bg-red-600 border border-red-500/40 text-red-300 hover:text-white p-2 rounded-full shadow-lg z-10 opacity-0 group-hover:opacity-100 transition-all cursor-pointer"
                          onClick={() => handleDelete(item?.id, item?.mediaUrl)}
                          title="Delete Media"
                        >
                          <FaTrash size={11} />
                        </button>

                        {item.mediaType === "video" ? (
                          <div className="relative aspect-video bg-[#0A0A0C] flex items-center justify-center">
                            <iframe
                              src={getYoutubeEmbedUrl(item?.mediaUrl)}
                              className="w-full h-full border-0 pointer-events-none"
                              title={item.caption || "YouTube Reel"}
                            />
                            <div className="absolute top-2 left-2 bg-[#0A0A0C]/80 border border-[#C8A165]/40 text-[#C8A165] p-1.5 rounded-lg">
                              <FaVideo size={11} />
                            </div>
                          </div>
                        ) : (
                          <div className="relative aspect-square overflow-hidden bg-[#0A0A0C]">
                            <img
                              src={item.mediaUrl}
                              alt={item.caption || "Gallery"}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                        )}

                        <div className="p-3.5 bg-[#121215] border-t border-[#C8A165]/15">
                          <p className="text-xs font-bold text-[#F8F5F2] truncate">
                            {item.caption || "Untitled Artwork"}
                          </p>
                          <span className="text-[0.62rem] text-[#C8A165] uppercase font-semibold tracking-wider block mt-0.5">
                            {item.categoryName || "Bridal Gallery"}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Admin Gallery Pagination Bar */}
              {totalAdminPages > 1 && (
                <div className="flex items-center justify-between gap-4 mt-8 pt-4 border-t border-[#C8A165]/15">
                  <span className="text-xs text-[#F8F5F2]/50">
                    Page <strong className="text-[#C8A165]">{adminPageNo}</strong> of{" "}
                    <strong className="text-[#C8A165]">{totalAdminPages}</strong>
                  </span>

                  <div className="flex items-center gap-2">
                    <button
                      disabled={adminPageNo === 1}
                      onClick={() => setAdminPageNo((prev) => Math.max(prev - 1, 1))}
                      className="px-3 py-1.5 rounded-xl border border-[#C8A165]/30 text-xs font-bold text-[#C8A165] hover:bg-[#C8A165] hover:text-[#0A0A0C] disabled:opacity-30 disabled:pointer-events-none transition-all flex items-center gap-1 cursor-pointer"
                    >
                      <FaArrowLeft size={10} /> Prev
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalAdminPages }, (_, i) => i + 1).map((pNum) => (
                        <button
                          key={pNum}
                          onClick={() => setAdminPageNo(pNum)}
                          className={`w-7 h-7 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                            adminPageNo === pNum
                              ? "bg-[#C8A165] text-[#0A0A0C]"
                              : "bg-[#18181D] text-[#F8F5F2]/70 hover:text-[#C8A165] border border-[#C8A165]/20"
                          }`}
                        >
                          {pNum}
                        </button>
                      ))}
                    </div>

                    <button
                      disabled={adminPageNo === totalAdminPages}
                      onClick={() => setAdminPageNo((prev) => Math.min(prev + 1, totalAdminPages))}
                      className="px-3 py-1.5 rounded-xl border border-[#C8A165]/30 text-xs font-bold text-[#C8A165] hover:bg-[#C8A165] hover:text-[#0A0A0C] disabled:opacity-30 disabled:pointer-events-none transition-all flex items-center gap-1 cursor-pointer"
                    >
                      Next <FaArrowRight size={10} />
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {(loading || galleryLoading) && <Loading />}
    </div>
  );
}
